import { formatDateISO } from '~/services/TimeService';

import { LENS_PERIOD_KEY, type LensFilterValues, type LensPeriod, type LensPeriodComparison } from '~/types/leLens';

import type { LensRange, LensSelection } from './cardView';
import { isLensPeriod } from './lensPeriodPresets';

/**
 * The part of a dashboard's state that belongs in its address.
 *
 * A dashboard is something people send each other, and "look at this board"
 * only means anything if the filters travel with the link. Everything here is
 * a viewer's own choice, so it lives in the URL rather than in the saved spec.
 */
export const LENS_VIEW_PARAM = 'lensView';

/**
 * The name this state used to travel under. It collided with the unified shell's own `view`
 * parameter: the shell wrote `view=lens` and this page then wrote its filters over the top of it,
 * or deleted it outright when the board sat at its defaults. Links kept working only because the
 * shell falls back to the Lens view whenever a lens id is present, so `?view=lensIndex` plus a
 * board filter was the losing case. Old links are still read from here.
 */
export const LEGACY_LENS_VIEW_PARAM = 'view';

const ENVELOPE_VERSION = 'v1';

export type LensUrlState = {
  filters: LensFilterValues;
  selections: LensSelection;
  /** Measure ranges the viewer dragged or typed, keyed by column. */
  ranges: Record<string, LensRange>;
};

/**
 * Whether the scope a link carried arrived whole.
 *
 * `shortened` is the case worth a sentence on screen: the address said it carried a scope, and it
 * did not survive the trip, so the recipient is looking at the board's default numbers under a
 * link that promised the sender's. Silently showing the default is the one outcome this journey
 * cannot allow.
 */
export type LensViewIntegrity = 'absent' | 'intact' | 'shortened';

const EMPTY: LensUrlState = { filters: {}, selections: {}, ranges: {} };

/** Links written before the period grammar carried the comparison as its own key. */
const LEGACY_COMPARISONS: Record<string, LensPeriodComparison> = {
  previousPeriod: 'previous',
  previousYear: { back: 1, unit: 'year' },
};

function isEmpty(value: Record<string, unknown>): boolean {
  return Object.keys(value).length === 0;
}

/**
 * Undoes what a chat client does to a bare URL.
 *
 * Slack renders a pasted link as `<url>` or `<url|label>` and a person writing "look at this:
 * <url>." leaves the trailing stop attached. None of that is part of the value, and all of it
 * arrives glued to the last parameter, which is this one.
 */
function unwrapPastedValue(raw: string): string {
  let value = raw.trim();
  if (value.startsWith('<')) {
    value = value.slice(1);
  }
  // A pipe inside a selected value is data. Slack's label begins after the JSON root.
  const rootEnd = value.lastIndexOf('}');
  if (rootEnd >= 0 && value[rootEnd + 1] === '|') {
    value = value.slice(0, rootEnd + 1);
  }
  return value.replace(/[>).\],;]+$/, '');
}

/**
 * Returns undefined when there is nothing worth putting in the address, so a
 * board at its defaults keeps a clean URL rather than carrying `?lensView={}`.
 *
 * The value is stamped `v1.<bytes>.<json>` so a link that lost its tail on the way here can be
 * told apart from a link that never carried a scope. The stamp costs eight characters and is the
 * only thing standing between a truncated link and a reader reading the wrong numbers.
 */
export function encodeLensView(state: LensUrlState): string | undefined {
  const filters = state.filters ?? {};
  const selections = state.selections ?? {};
  const ranges = state.ranges ?? {};
  if (isEmpty(filters) && isEmpty(selections) && isEmpty(ranges)) {
    return undefined;
  }
  const body = JSON.stringify({
    ...(isEmpty(filters) ? {} : { f: filters }),
    ...(isEmpty(selections) ? {} : { s: selections }),
    ...(isEmpty(ranges) ? {} : { r: ranges }),
  });
  return `${ENVELOPE_VERSION}.${body.length}.${body}`;
}

/** Splits the stamp off a stamped value. Unstamped values are older links and carry no stamp. */
function readEnvelope(value: string): { body: string; declaredLength: number | null; valid: boolean } {
  if (!value.startsWith(`${ENVELOPE_VERSION}.`)) {
    return { body: value, declaredLength: null, valid: value.startsWith('{') };
  }
  const afterVersion = value.slice(ENVELOPE_VERSION.length + 1);
  const dot = afterVersion.indexOf('.');
  if (dot <= 0) {
    return { body: '', declaredLength: null, valid: false };
  }
  const declared = Number(afterVersion.slice(0, dot));
  return {
    body: afterVersion.slice(dot + 1),
    declaredLength: declared,
    valid: /^\d+$/.test(afterVersion.slice(0, dot)) && Number.isSafeInteger(declared),
  };
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const safeKeys = (value: Record<string, unknown>, keys?: Array<string>) =>
  Object.keys(value).every(
    (key) => !['__proto__', 'constructor', 'prototype'].includes(key) && (!keys || keys.includes(key)),
  );
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const count = (value: unknown, maximum: number, minimum = 1) =>
  finite(value) && Number.isInteger(value) && value >= minimum && value <= maximum;
const civilDate = (value: unknown) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && formatDateISO(value) === value;
const PERIOD_UNITS = ['hour', 'day', 'week', 'month', 'quarter', 'year', 'fiscalYear'];
// Keep these wire forms aligned with svc-reporting's le-lens/period.ts validators.
const wallTime = (value: unknown): value is string => {
  if (
    typeof value !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value) ||
    !civilDate(value.slice(0, 10))
  ) {
    return false;
  }
  const [hour, minute, second = 0] = value.slice(11).split(':').map(Number);
  return hour < 24 && minute < 60 && second < 60;
};
const fixedPeriod = (value: Record<string, unknown>) =>
  safeKeys(value, ['from', 'to']) &&
  Object.keys(value).length === 2 &&
  ((civilDate(value.from) && civilDate(value.to) && (value.from as string) <= (value.to as string)) ||
    (wallTime(value.from) && wallTime(value.to) && value.from < value.to));

function validBounds(value: unknown, numeric: boolean): boolean {
  if (!record(value) || !safeKeys(value, numeric ? ['min', 'max'] : ['from', 'to'])) {
    return false;
  }
  if (!Object.values(value).every(numeric ? finite : civilDate)) {
    return false;
  }
  return numeric
    ? value.min === undefined || value.max === undefined || Number(value.min) <= Number(value.max)
    : value.from === undefined || value.to === undefined || (value.from as string) <= (value.to as string);
}

function validPeriod(value: unknown): boolean {
  if (
    !record(value) ||
    !safeKeys(value, ['anchor', 'window', 'comparison']) ||
    !['anchor', 'window', 'comparison'].some((key) => key in value)
  ) {
    return false;
  }
  const anchor = value.anchor ?? undefined;
  const window = value.window ?? undefined;
  const { comparison } = value;
  if (anchor === undefined && window === undefined && comparison === undefined) {
    return false;
  }
  if (
    anchor !== undefined &&
    !['yesterday', 'today', 'now', 'lastHour'].includes(anchor as string) &&
    !(
      record(anchor) &&
      Object.keys(anchor).length === 1 &&
      ((safeKeys(anchor, ['date']) && civilDate(anchor.date)) || (safeKeys(anchor, ['at']) && wallTime(anchor.at)))
    )
  ) {
    return false;
  }
  if (window !== undefined && window !== 'all') {
    if (!record(window)) {
      return false;
    }
    const keys = Object.keys(window);
    if (
      !(
        (keys.length === 1 && count(window.days, 3660)) ||
        (keys.length === 1 && count(window.hours, 744)) ||
        (keys.length === 1 && PERIOD_UNITS.slice(2).includes(window.toDate as string)) ||
        (safeKeys(window, ['calendar', 'back', 'count']) &&
          PERIOD_UNITS.includes(window.calendar as string) &&
          (window.back === undefined || count(window.back, 120, 0)) &&
          (window.count === undefined || count(window.count, 120))) ||
        fixedPeriod(window)
      )
    ) {
      return false;
    }
  }
  if (window === 'all' && comparison) {
    return false;
  }
  return (
    comparison === undefined ||
    comparison === null ||
    comparison === 'previous' ||
    (record(comparison) &&
      ((safeKeys(comparison, ['back', 'unit']) &&
        count(comparison.back, 120) &&
        PERIOD_UNITS.includes(comparison.unit as string)) ||
        fixedPeriod(comparison)))
  );
}

function validScope(value: unknown): value is Record<string, unknown> {
  if (!record(value) || !safeKeys(value, ['f', 's', 'r', 'c'])) {
    return false;
  }
  if (
    value.c !== undefined &&
    (typeof value.c !== 'string' || !Object.prototype.hasOwnProperty.call(LEGACY_COMPARISONS, value.c))
  ) {
    return false;
  }
  for (const key of ['f', 's', 'r']) {
    const entries = value[key];
    if (entries === undefined) {
      continue;
    }
    if (!record(entries) || !safeKeys(entries)) {
      return false;
    }
    if (
      !Object.entries(entries).every(([id, entry]) => {
        if (key === 's') {
          return Array.isArray(entry) && entry.every((item) => typeof item === 'string');
        }
        if (key === 'r') {
          return validBounds(entry, true);
        }
        if (id === LENS_PERIOD_KEY) {
          return validPeriod(entry);
        }
        return (
          typeof entry === 'string' ||
          typeof entry === 'boolean' ||
          (Array.isArray(entry) && entry.every((item) => typeof item === 'string')) ||
          validBounds(entry, true) ||
          validBounds(entry, false)
        );
      })
    ) {
      return false;
    }
  }
  return true;
}

function readScope(raw: string): Record<string, unknown> | null {
  const envelope = readEnvelope(unwrapPastedValue(raw));
  if (!envelope.valid || (envelope.declaredLength !== null && envelope.body.length !== envelope.declaredLength)) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(envelope.body);
    return validScope(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Says whether a link's scope arrived whole, without deciding what to do about it.
 *
 * Legacy JSON has no length stamp, but its complete scope still has to be readable. A damaged
 * envelope or invalid nested value must not silently turn an exact scope into partial defaults.
 */
export function lensViewIntegrity(raw: string | null | undefined): LensViewIntegrity {
  if (!raw?.trim()) {
    return 'absent';
  }
  return readScope(raw) ? 'intact' : 'shortened';
}

/**
 * Tolerant by design. A link can be truncated by a chat client, hand-edited, or
 * left over from an older shape of the board, and none of those should be an
 * error page: an unreadable view parameter just means the default view.
 *
 * {@link lensViewIntegrity} is what tells the reader it happened. This function's job is only to
 * keep the board on screen.
 */
export function decodeLensView(raw: string | null | undefined): LensUrlState {
  if (!raw) {
    return EMPTY;
  }
  const parsed = readScope(raw);
  if (!parsed) {
    return EMPTY;
  }

  const source = parsed as { f?: unknown; s?: unknown; r?: unknown; c?: unknown };
  const filters: LensFilterValues = {};
  if (source.f && typeof source.f === 'object' && !Array.isArray(source.f)) {
    Object.entries(source.f as Record<string, unknown>).forEach(([key, value]) => {
      // A filter value is a string, a list of strings, or a date range object.
      // Anything else came from somewhere this code does not control.
      if (typeof value === 'string' || typeof value === 'boolean') {
        filters[key] = value;
      } else if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
        filters[key] = value;
      } else if (key === LENS_PERIOD_KEY && isLensPeriod(value)) {
        const { anchor, window, ...rest } = value;
        filters[key] = {
          ...rest,
          ...(anchor != null && !(typeof window === 'object' && window && 'to' in window) ? { anchor } : {}),
          ...(window != null ? { window } : {}),
        } as LensPeriod;
      } else if (value && typeof value === 'object') {
        if (Object.keys(value).length === 0 || ['min', 'max', 'from', 'to'].some((bound) => bound in value)) {
          filters[key] = value;
        }
      }
    });
  }

  const selections: LensSelection = {};
  if (source.s && typeof source.s === 'object' && !Array.isArray(source.s)) {
    Object.entries(source.s as Record<string, unknown>).forEach(([key, value]) => {
      if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
        selections[key] = value;
      }
    });
  }

  // A range narrows what a card shows just as much as a filter does, so a link that drops it
  // shows the recipient different numbers under the same address. Only finite numbers are taken:
  // a hand-edited bound of "abc" is not a bound.
  const ranges: Record<string, LensRange> = {};
  if (source.r && typeof source.r === 'object' && !Array.isArray(source.r)) {
    Object.entries(source.r as Record<string, unknown>).forEach(([key, value]) => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return;
      }
      const bound = value as { min?: unknown; max?: unknown };
      const range: LensRange = {
        ...(typeof bound.min === 'number' && Number.isFinite(bound.min) ? { min: bound.min } : {}),
        ...(typeof bound.max === 'number' && Number.isFinite(bound.max) ? { max: bound.max } : {}),
      };
      if (range.min !== undefined || range.max !== undefined) {
        ranges[key] = range;
      }
    });
  }

  const legacyComparison = typeof source.c === 'string' ? LEGACY_COMPARISONS[source.c] : undefined;
  if (legacyComparison && !(LENS_PERIOD_KEY in filters)) {
    filters[LENS_PERIOD_KEY] = { comparison: legacyComparison } as LensPeriod;
  }

  return { filters, selections, ranges };
}

/** True for a value that was written by this module rather than by the shell's own `view` param. */
function looksLikeLensView(value: string | null): boolean {
  if (!value) {
    return false;
  }
  const trimmed = unwrapPastedValue(value);
  return trimmed.startsWith(`${ENVELOPE_VERSION}.`) || trimmed.startsWith('{');
}

/** The raw scope value a search string carries, preferring the current name over the old one. */
export function lensViewParamValue(search: URLSearchParams | string): string | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const current = params.get(LENS_VIEW_PARAM);
  if (current) {
    return current;
  }
  const legacy = params.get(LEGACY_LENS_VIEW_PARAM);
  return looksLikeLensView(legacy) ? legacy : null;
}

/** {@link decodeLensView} over whichever parameter the link actually used. */
export function readLensView(search: URLSearchParams | string): LensUrlState {
  return decodeLensView(lensViewParamValue(search));
}

/**
 * Applies the viewer's scope to a copy of `current`, leaving every other parameter alone.
 *
 * The legacy name is cleared only when it is holding a scope, so migrating an old link does not
 * take the unified shell's `view=lens` with it.
 */
export function applyLensView(current: URLSearchParams, state: LensUrlState): URLSearchParams {
  const next = new URLSearchParams(current);
  const encoded = encodeLensView(state);
  if (encoded) {
    next.set(LENS_VIEW_PARAM, encoded);
  } else {
    next.delete(LENS_VIEW_PARAM);
  }
  if (looksLikeLensView(next.get(LEGACY_LENS_VIEW_PARAM))) {
    next.delete(LEGACY_LENS_VIEW_PARAM);
  }
  return next;
}
